import { Button } from '@/components/ui/button';
import {
  SEAT_KEY_LABELS,
  TEST_STATUS_WORDING,
  TEST_STUBBED_NOTICE,
  TEST_WORDING,
} from '@/lib/model-labels';
import type { ModelTestResult, ModelTestStatus } from '@matter_extractor/shared';
import { MODEL_SEAT_KEYS, seatFingerprint } from '@matter_extractor/shared';

/**
 * « Tester les 5 sièges » (§11.1) - the count read off `MODEL_SEAT_KEYS`, so
 * the button says how many seats it probes whatever the fleet holds (#149).
 *
 * One throwaway call per seat, on the values the FORM holds. It writes nothing:
 * no model moves, no journal line is added, so it can be fired as often as
 * somebody likes while a form sits unsaved - and that is the whole point, since
 * the question it answers is « est-ce que ce que je m'apprête à enregistrer
 * tient ? » rather than « est-ce que ce qui tourne tient ? ».
 *
 * It reports and never blocks, the régime of every warning on this screen
 * (P2/Q12): an operator who wants to pin a model this deploy cannot reach today
 * - because the key arrives tomorrow - is doing something legitimate, and a
 * button that closed the form would make the screen unusable for it.
 *
 * Every sentence is worded from the `code` (variantes §11.2), out of
 * `TEST_WORDING`, which is a `Record` on the closed set: an eighth code without
 * its sentence stops compiling.
 */

export interface ModelConnectionTestProps {
  results: readonly ModelTestResult[] | null;
  running: boolean;
  /** The call itself failed - a refused token, a backend that is not there. */
  failed: boolean;
  onRun: () => void;
}

export function ModelConnectionTest({ results, running, failed, onRun }: ModelConnectionTestProps) {
  return (
    <section
      aria-labelledby="test-connexion"
      className="space-y-4 rounded-md border-2 border-gray-200 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 id="test-connexion" className="font-semibold text-bleu-nuit text-lg">
            Vérifier la connexion
          </h2>
          <p className="max-w-2xl text-gray-700 text-sm">
            Un appel d’essai par siège, sur ce que porte le formulaire - y compris ce que vous venez
            de changer sans l’avoir enregistré. Rien n’est écrit, rien n’entre au journal.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onRun} disabled={running}>
          {running ? 'Appel en cours…' : `Tester les ${MODEL_SEAT_KEYS.length} sièges`}
        </Button>
      </div>

      {failed ? (
        <p className="text-gray-700 text-sm">
          Le test n’a pas pu être lancé. Les modèles de la base n’ont pas bougé.
        </p>
      ) : null}

      {results === null ? (
        <p className="text-gray-600 text-sm">
          Aucun essai depuis l’ouverture de l’écran : ce qui suit n’est vérifié par personne tant
          que le bouton n’a pas été utilisé.
        </p>
      ) : (
        <>
          {/*
            The deploy calls nobody, said ONCE above the list rather than only
            in the four small lines inside it (BUILD-866). Same shape as the
            Langfuse section, which names its own absence in one sentence - and
            OUTSIDE the list, because the list holds seat results and this is
            not one.

            `some` and not `every`, and the difference is a real case rather
            than a defensive habit: `stubbed` is a property of the DEPLOY, so
            the seats are stubbed together - but `sampling-incoherent` is
            decided before it and preempts one of them. On `every`, one
            incoherent pair would take the sentence away from the seats
            still saying nobody was called.
          */}
          {results.some((result) => result.code === 'stub') ? (
            <p className="text-gray-700 text-sm">{TEST_STUBBED_NOTICE}</p>
          ) : null}
          <ul className="divide-y divide-gray-200">
            {results.map((result) => (
              <li key={result.seat} className="flex flex-wrap items-baseline gap-x-2 py-2 text-sm">
                <span className={`font-semibold ${STATUS_TONE[result.status]}`}>
                  {TEST_STATUS_WORDING[result.status]}
                </span>
                <span className="font-semibold text-gray-900">{SEAT_KEY_LABELS[result.seat]}</span>
                <span className="text-gray-700">
                  <code>{seatFingerprint(result.pin)}</code> - {TEST_WORDING[result.code]}
                  {result.latencyMs === null ? null : ` (${formatLatency(result.latencyMs)})`}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * How loudly each severity is painted, as a `Record` on the closed set: a
 * fifth status without its tone stops compiling, the régime of every wording
 * table on this screen.
 *
 * `skipped` is deliberately the QUIETEST of the four, gray rather than
 * bleu-nuit: it is the one line that reports nothing having happened, and
 * painting it in the primary colour would put a stubbed deploy at the same
 * visual weight as a door that actually answered.
 */
const STATUS_TONE: Record<ModelTestStatus, string> = {
  ok: 'text-bleu-nuit',
  warning: 'text-bleu-nuit',
  error: 'text-danger',
  skipped: 'text-gray-600',
};

/**
 * A round trip, in the unit that says something about it.
 *
 * Seconds below one would print `0,0 s` on every fast answer - and on the
 * scripted stub, which answers instantly, all four lines would read as a bug
 * rather than as a success.
 */
function formatLatency(latencyMs: number): string {
  if (latencyMs < 1000) return `${latencyMs} ms`;
  return `${(latencyMs / 1000).toFixed(1).replace('.', ',')} s`;
}
